import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { PublicProfile, UserProfile, Review } from '../types';
import { useTranslation } from '../lib/translations';
import { 
  MapPin, 
  Video, 
  Clock, 
  ArrowLeft, 
  Globe, 
  CheckCircle2, 
  CreditCard, 
  MessageSquare,
  Sparkles,
  Zap,
  PlayCircle,
  ExternalLink,
  ChevronRight,
  ShieldCheck,
  Award,
  Loader2,
  Hash,
  Store,
  CircleDollarSign,
  Languages,
  Sun,
  Moon,
  Layout,
  Edit3,
  Star,
  User
} from 'lucide-react';
import { format } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import Avatar from '../components/Avatar';
import Tooltip from '../components/Tooltip';

interface PublicProfileViewProps {
  user: any;
  profile: UserProfile | null;
}

const PublicProfileView: React.FC<PublicProfileViewProps> = ({ user, profile }) => {
  const { id } = useParams<{ id: string }>();
  const { t, language, setLanguage } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [teacher, setTeacher] = useState<PublicProfile | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDark, setIsDark] = useState(document.documentElement.classList.contains('dark'));
  const reviewsRef = useRef<HTMLDivElement>(null);

  const isOwner = auth.currentUser?.uid === id;
  const locale = language === 'fr' ? fr : enUS;

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  useEffect(() => {
    const fetchTeacher = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const d = await getDoc(doc(db, 'public_profiles', id));
        if (d.exists()) {
          const data = d.data() as PublicProfile;
          if (data.isPublished || isOwner) {
            setTeacher(data);
          } else {
            navigate('/teachers');
          }
        } else {
          navigate('/teachers');
        }

        // Fetch reviews
        const revQ = query(collection(db, 'reviews'), where('teacherId', '==', id), orderBy('updatedAt', 'desc'));
        const revSnap = await getDocs(revQ);
        setReviews(revSnap.docs.map(doc => ({ ...doc.data() } as Review)));

      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    fetchTeacher();
  }, [id, navigate, isOwner]);

  const scrollToReviews = () => {
    reviewsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const avgRating = reviews.length > 0 
    ? (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  if (loading) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
      <Loader2 className="animate-spin text-purple-600" size={48} />
      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{t.common.loading}</p>
    </div>
  );

  if (!teacher) return null;

  const content = (
    <div className="max-w-6xl mx-auto space-y-12 pb-32">
      <div className="flex items-center justify-between">
        <button 
          onClick={() => isOwner ? navigate('/public-profile') : navigate('/teachers')}
          className="flex items-center gap-2 text-slate-500 hover:text-purple-600 transition-all text-[10px] font-black uppercase tracking-[0.2em] group"
        >
          <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" /> 
          {isOwner ? t.common.backToEditor : t.common.back}
        </button>

        {isOwner && (
          <div className="flex items-center gap-3 animate-in fade-in slide-in-from-right-4 duration-500">
             <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${teacher.isPublished ? 'bg-teal-500/10 text-teal-600 border-teal-500/20' : 'bg-amber-500/10 text-amber-600 border-amber-500/20'}`}>
               {teacher.isPublished ? t.common.liveProfile : t.common.draftMode}
             </span>
             <button 
               onClick={() => navigate('/public-profile')}
               className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-[9px] font-black uppercase tracking-widest shadow-lg shadow-purple-500/20 hover:bg-purple-700 transition-all"
             >
               <Edit3 size={12} /> {t.common.editProfile}
             </button>
          </div>
        )}
      </div>

      <div className="relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-3xl overflow-hidden shadow-2xl">
        {!teacher.isPublished && (
          <div className="absolute top-4 right-4 z-30 px-3 py-1 bg-amber-500 text-white text-[10px] font-black uppercase tracking-widest rounded-full shadow-lg border-2 border-white dark:border-slate-900 animate-pulse">
            {t.common.draftPreview}
          </div>
        )}
        <div className="h-48 md:h-64 bg-gradient-to-r from-purple-800 via-indigo-800 to-slate-900 relative">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>
          <div className="absolute bottom-[-60px] left-10 p-1.5 bg-white dark:bg-slate-900 rounded-3xl shadow-2xl">
            <Avatar 
              src={teacher.avatarUrl} 
              name={teacher.displayName} 
              className="w-32 h-32 md:w-48 md:h-48 rounded-2xl" 
              iconSize={64}
            />
          </div>
        </div>
        
        <div className="pt-20 pb-10 px-10 md:pl-64 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div className="space-y-4 max-w-xl">
            <div>
               <div className="flex items-center gap-4 mb-2">
                  <h1 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tighter leading-none">{teacher.displayName}</h1>
                  {avgRating && (
                    <button 
                      onClick={scrollToReviews}
                      className="flex items-center gap-1.5 px-3 py-1 bg-amber-50 dark:bg-amber-900/20 text-amber-600 rounded-lg border border-amber-200 dark:border-amber-500/20 hover:scale-105 transition-all"
                    >
                      <Star size={14} fill="currentColor" />
                      <span className="text-sm font-black">{avgRating}</span>
                      <span className="text-[10px] font-bold opacity-60">({reviews.length})</span>
                    </button>
                  )}
               </div>
               <p className="text-xl font-bold text-purple-600 dark:text-purple-400 tracking-tight italic opacity-90">{teacher.tagline}</p>
            </div>
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-500">
                <MapPin size={16} className="text-purple-600" /> {teacher.location.city}, {teacher.location.country}
              </div>
              {teacher.location.remote && (
                <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-teal-600">
                  <Video size={16} /> {t.common.remote}
                </div>
              )}
              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-indigo-600">
                <Award size={16} /> {t.publicProfile.certifiedEducator}
              </div>
            </div>
          </div>
          
          <div className="flex flex-col gap-3 shrink-0">
             <div className="bg-slate-50 dark:bg-slate-950 p-4 rounded-2xl border border-slate-100 dark:border-white/5 text-center shadow-inner">
                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">{t.marketplace.startingFrom}</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-black text-slate-900 dark:text-white">€{teacher.pricing.hourlyRate}</span>
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-tighter">{t.marketplace.perHour}</span>
                </div>
             </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-12">
          <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-3xl p-10 shadow-sm">
             <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight mb-6 flex items-center gap-3">
               <Sparkles size={24} className="text-purple-600" /> {t.publicProfile.bio}
             </h2>
             <p className="text-slate-600 dark:text-slate-400 text-lg leading-relaxed font-medium whitespace-pre-wrap">
               {teacher.bio}
             </p>
          </section>

          {teacher.youtubeVideoId && (
            <section className="bg-slate-900 rounded-3xl overflow-hidden shadow-2xl p-2">
               <div className="aspect-video relative rounded-2xl overflow-hidden">
                 <iframe 
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${teacher.youtubeVideoId}`}
                  title="Teacher Intro"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                 />
               </div>
            </section>
          )}

          {/* Student Reviews Section */}
          <section ref={reviewsRef} className="space-y-8 scroll-mt-24">
             <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
               <MessageSquare size={28} className="text-amber-500" /> {t.reviews.feedbackTitle}
             </h2>
             
             {reviews.length === 0 ? (
               <div className="bg-white dark:bg-slate-900 border-2 border-dashed border-slate-100 dark:border-white/5 rounded-3xl p-20 text-center">
                 <Star size={48} className="text-slate-100 dark:text-slate-800 mx-auto mb-4" />
                 <p className="text-slate-400 font-bold italic">{t.reviews.feedbackEmpty}</p>
               </div>
             ) : (
               <div className="space-y-6">
                 {reviews.map((rev, i) => (
                   <div key={i} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-3xl p-8 shadow-sm group">
                      <div className="flex items-start justify-between gap-4 mb-6">
                         <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center text-slate-400">
                               <User size={20} />
                            </div>
                            <div>
                               <p className="text-sm font-black text-slate-900 dark:text-white">{rev.studentName}</p>
                               <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{format(rev.updatedAt.toDate(), 'MMMM yyyy', { locale })}</p>
                            </div>
                         </div>
                         <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <Star key={s} size={14} fill={s <= rev.rating ? 'currentColor' : 'none'} className={s <= rev.rating ? 'text-amber-400' : 'text-slate-100 dark:text-slate-800'} />
                            ))}
                         </div>
                      </div>
                      <p className="text-slate-600 dark:text-slate-300 font-medium leading-relaxed italic">"{rev.comment}"</p>
                   </div>
                 ))}
               </div>
             )}
          </section>
        </div>

        <aside className="lg:col-span-4 space-y-8">
          <div className="sticky top-24 space-y-8">
            {/* Teaching Focus Card */}
            {teacher.instruments && teacher.instruments.length > 0 && (
              <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-3xl p-8 shadow-sm">
                <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight mb-6 flex items-center gap-3">
                  <Layout size={20} className="text-indigo-600" /> {t.publicProfile.curriculum}
                </h2>
                <div className="flex flex-wrap gap-2">
                  {teacher.instruments.map((inst, i) => (
                    <span 
                      key={i} 
                      className="px-4 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-700 dark:text-slate-200 text-xs font-black uppercase tracking-widest border border-slate-200 dark:border-white/5 shadow-sm transition-all cursor-default"
                    >
                      {inst}
                    </span>
                  ))}
                </div>
              </section>
            )}

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-3xl p-8 shadow-xl space-y-8">
              <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                <CreditCard size={20} className="text-teal-600" /> {t.publicProfile.pricing}
              </h3>

              <div className="space-y-3">
                {teacher.pricing.lessonPacks.map((pack, i) => (
                  <div key={i} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-white/5 rounded-2xl">
                    <div className="space-y-1">
                      <p className="text-xs font-black uppercase tracking-widest text-slate-900 dark:text-white">{pack.label}</p>
                      {pack.savings && <span className="text-[9px] font-black bg-teal-500 text-white px-2 py-0.5 rounded-full">{pack.savings} {language === 'fr' ? 'ÉCONOMIE' : 'SAVINGS'}</span>}
                    </div>
                    <div className="text-right">
                      <span className="text-xl font-black text-teal-600">€{pack.price}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                {profile ? (
                  <button 
                    onClick={() => navigate('/support')}
                    className="w-full py-4 bg-purple-600 text-white rounded-2xl font-black text-sm uppercase tracking-widest flex items-center justify-center gap-3 shadow-xl shadow-purple-500/30 hover:bg-purple-700 hover:scale-105 transition-all active:scale-95"
                  >
                    <Clock size={20} /> {t.common.book}
                  </button>
                ) : (
                  <Link 
                    to="/login"
                    className="w-full py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-3 shadow-xl hover:opacity-90 transition-all text-center"
                  >
                    <Zap size={18} /> {t.publicProfile.mustSignIn}
                  </Link>
                )}
                <button 
                  onClick={() => navigate('/support')}
                  className="w-full py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-white/5 rounded-2xl font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all"
                >
                  <MessageSquare size={16} /> {t.common.contact}
                </button>
              </div>
            </div>

            <div className="bg-teal-50 dark:bg-teal-900/10 border border-teal-100 dark:border-teal-500/20 p-6 rounded-2xl flex gap-4 items-start shadow-inner">
               <ShieldCheck size={24} className="text-teal-600 shrink-0 mt-1" />
               <div>
                  <h4 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest mb-1">{t.publicProfile.safeVerified}</h4>
                  <p className="text-xs text-slate-500 font-medium leading-relaxed">{t.publicProfile.safeVerifiedDesc}</p>
               </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );

  if (!profile) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#020617] transition-colors duration-300">
        <nav className="fixed top-6 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] md:w-[calc(100%-3rem)] max-w-6xl z-50 glass-card rounded-2xl px-6 h-16 flex items-center justify-between shadow-2xl border-white/40 dark:border-white/5">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="p-2 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-lg shadow-lg group-hover:scale-105 transition-transform">
              <Hash size={18} className="text-white" />
            </div>
            <span className="hidden sm:block text-lg font-black tracking-tight text-slate-900 dark:text-white">SharpNote</span>
          </Link>
          <div className="flex items-center gap-4 md:gap-8">
            <Link to="/teachers" className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-colors ${location.pathname === '/teachers' ? 'text-purple-600 dark:text-purple-400' : 'text-slate-500 dark:text-slate-400 hover:text-purple-600'}`}><Store size={16} />{t.nav.marketplace}</Link>
            <Link to="/pricing" className={`hidden md:flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-colors ${location.pathname === '/pricing' ? 'text-purple-600 dark:text-purple-400' : 'text-slate-500 dark:text-slate-400 hover:text-purple-600'}`}><CircleDollarSign size={16} />{t.nav.pricing}</Link>
            <div className="flex items-center gap-3">
              <button onClick={() => setLanguage(language === 'en' ? 'fr' : 'en')} className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-all text-[10px] font-black uppercase tracking-widest"><Languages size={16} />{language.toUpperCase()}</button>
              <button onClick={() => setIsDark(!isDark)} className="p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-all">{isDark ? <Sun size={18} /> : <Moon size={18} />}</button>
              <Link to="/login" className="px-6 py-2 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-950 font-black text-[10px] uppercase tracking-widest hover:scale-105 transition-all shadow-xl">{t.landing.enterApp}</Link>
            </div>
          </div>
        </nav>
        <div className="fixed inset-0 overflow-hidden pointer-events-none"><div className="absolute -top-[15%] left-[20%] w-[60%] h-[60%] bg-purple-600/10 dark:bg-purple-600/20 blur-[120px] rounded-full opacity-40"></div></div>
        <main className="pt-32 pb-20 px-6 max-w-7xl mx-auto">{content}</main>
      </div>
    );
  }

  return content;
};

export default PublicProfileView;
